import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLang } from "@/i18n/LanguageContext";
import { RARITY_COLORS, nextWindow } from "@/lib/trolls";
import { TrollArt } from "@/components/landing/TrollArt";
import { StoreLink } from "@/components/landing/StoreLinks";

const Row = ({ label, children, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-dashed border-line text-sm" data-testid={testId}>
    <dt className="font-bold text-ink-muted">{label}</dt>
    <dd className="text-ink text-right">{children}</dd>
  </div>
);

export default function JournalDialog({ troll, onClose }) {
  const { t, tl, fmtDate, fmtNum } = useLang();
  if (!troll) return null;
  const win = troll.window_start ? nextWindow(troll.window_start, troll.window_end) : null;
  const same = win && win.start.getTime() === win.end.getTime();

  return (
    <Dialog open={!!troll} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t.map.close} data-testid="troll-journal-dialog" className="bg-parchment border-line rounded-[20px] max-w-lg p-0 overflow-hidden">
        <div className="p-7 pb-5 flex gap-5 items-end bg-paper border-b border-line">
          <TrollArt troll={troll} className="h-40 w-28 shrink-0" testId="troll-journal-art" />
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-ink-muted">{t.journal.entry} · {troll.id}</div>
            <DialogTitle className="font-display text-3xl font-semibold text-ink mt-1" data-testid="troll-journal-name">{troll.name}</DialogTitle>
            <div className="font-bold text-rowan">{tl(troll, "epithet")}</div>
          </div>
        </div>
        <div className="p-7 pt-5">
          <DialogDescription className="font-display italic text-lg text-ink leading-relaxed">
            “{tl(troll, "blurb")}”
          </DialogDescription>
          <dl className="mt-5">
            <Row label={t.rarity[troll.rarity]}>
              <span className="inline-flex items-center gap-2 font-bold" style={{ color: RARITY_COLORS[troll.rarity] }}>{t.category[troll.category]}</span>
            </Row>
            <Row label={t.journal.xp}>{fmtNum(troll.xp)} XP</Row>
            <Row label={t.journal.where}>
              {troll.route_segment}
              {troll.waterfall_id && ` · ${t.journal.waterfall} ${troll.waterfall_id}`}
            </Row>
            <Row label={t.journal.access} testId="troll-journal-access">{t.access[troll.access]}</Row>
            {win && (
              <Row label={t.journal.window} testId="troll-journal-window">
                {t.festivals[troll.festival_name]}: {fmtDate(win.start)}{!same && ` – ${fmtDate(win.end)}`}
                {win.live && <span className="ml-2 stamp !text-[10px] !py-0.5">{t.journal.liveNow}</span>}
              </Row>
            )}
            {troll.weekend_only && <Row label={t.journal.window}>{t.journal.weekend}</Row>}
          </dl>
          <StoreLink testId="troll-journal-cta" className="mt-6 w-full inline-flex justify-center rounded-full bg-moss hover:bg-moss-dark text-parchment px-6 py-3.5 font-extrabold transition-colors">
            {t.journal.catchIt}
          </StoreLink>
        </div>
      </DialogContent>
    </Dialog>
  );
}
